
import { FC, ReactNode } from "react"

import AssetDataBasicData from "./AssetDataBasicData"
import Asset from "models/Asset"
import { Flex, FlexItem } from "components/Flex"
import AssetDataFeatures from "./AssetDataFeatures"
import AssetDataLocation from "./AssetDataLocation"
import AssetDataOrganisationalAttributes from "./AssetDataOrganisationalAttributes"
import { ArticleAny } from "@/types"
import AssetDataTechnicalData from "./AssetDataTechnicalData"
import AssetDataFinance from "./AssetDataFinance"
import TenantEmployee from "models/TenantEmployee"
import { IUseAssetInitialSaleOrRental } from "hooks/useAssetInitialSaleOrRental"
import ConnectedAsset from "models/ConnectedAsset"
import Color from "models/Color"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import { TenantWarehouse } from "models/TenantWarehouse"
import { AssetStep } from "pages/AssetPage/config"
import { IAssetSummaryInvalidFields } from "components/AssetSummary/AssetSummary"
import { AssetDataContextProvider, IAssetInternalNotesEdit } from "./AssetDataContext"
import Address from "models/Address"

interface IAssetDataProps {
    asset: Asset
    article: ArticleAny
    operationHoursLogButton: ReactNode
    section?: AssetStep
    tenantEmployees: TenantEmployee[] | undefined
    initialSaleOrRental: IUseAssetInitialSaleOrRental | undefined
    color: Color | null
    eventAddress?: Address
    connectedAssets?: ConnectedAsset[]
    displayStatus?: AssetDisplayStatus
    displayStatusLoading?: boolean
    homebaseWarehouse?: TenantWarehouse | null
    homebaseWarehouseDescription?: string | null
    discountCalculated?: number | null
    salesPrice?: number | null
    salesPriceAdjustedCondition?: number | null
    invalidFields?: IAssetSummaryInvalidFields
    copyFeatures?: boolean
    placement: "sidebar" | "summary"
    pinnable?: boolean
    onEditClick?: (step: AssetStep) => void
    getEditLink?: (step: AssetStep) => string
    internalNotesEdit?: IAssetInternalNotesEdit
}

const AssetData: FC<IAssetDataProps> = ({
    asset,
    article,
    operationHoursLogButton,
    section,
    tenantEmployees,
    initialSaleOrRental,
    color,
    eventAddress,
    connectedAssets,
    displayStatus,
    displayStatusLoading,
    homebaseWarehouse,
    homebaseWarehouseDescription,
    discountCalculated,
    salesPrice,
    salesPriceAdjustedCondition,
    invalidFields,
    copyFeatures,
    placement,
    pinnable,
    onEditClick,
    getEditLink,
    internalNotesEdit,
}) => {
    return (
        <AssetDataContextProvider
            asset={asset}
            article={article}
            placement={placement}
            invalidFields={invalidFields}
            pinnable={pinnable}
            onEditClick={onEditClick}
            getEditLink={getEditLink}
            internalNotesEdit={internalNotesEdit}>
            <Flex flow="column" gap="4.8rem">
                {(!section || section === AssetStep.basicData) &&
                    <FlexItem>
                        <AssetDataBasicData />
                    </FlexItem>
                }
                {(!section || section === AssetStep.organisationalAttributes) &&
                    <FlexItem>
                        <AssetDataOrganisationalAttributes
                            tenantEmployees={tenantEmployees}
                            initialSaleOrRental={initialSaleOrRental}
                        />
                    </FlexItem>
                }
                {(!section || section === AssetStep.technicalData) &&
                    <FlexItem>
                        <AssetDataTechnicalData
                            operationHoursLogButton={operationHoursLogButton}
                            color={color}
                        />
                    </FlexItem>
                }
                {(!section || section === AssetStep.features) &&
                    <FlexItem>
                        <AssetDataFeatures
                            connectedAssets={connectedAssets}
                            copyFeatures={copyFeatures}
                        />
                    </FlexItem>
                }
                {(!section || section === AssetStep.location) &&
                    <FlexItem>
                        <AssetDataLocation
                            displayStatus={displayStatus}
                            displayStatusLoading={displayStatusLoading}
                            homebaseWarehouse={homebaseWarehouse}
                            homebaseWarehouseDescription={homebaseWarehouseDescription}
                            eventAddress={eventAddress}
                        />
                    </FlexItem>
                }
                {asset.hasFinancialData && (!section || section === AssetStep.finance) &&
                    <FlexItem>
                        <AssetDataFinance
                            discountCalculated={discountCalculated}
                            salesPrice={salesPrice}
                            salesPriceAdjustedCondition={salesPriceAdjustedCondition}
                        />
                    </FlexItem>
                }
            </Flex>
        </AssetDataContextProvider>
    )
}

export default AssetData